import { tx as translateInterfaceText } from '@/utils/i18n';
import { PrimeTimesMatrix, PrimeTimeMetric, ConversionMetric, PrimeTimeData, PrimeTimeDataExtended, WEEKDAYS, HOUR_BUCKETS, WeekdayIndex } from '../../hooks/types/primeTimesTypes';

export interface ChampionWindow {
  weekday: WeekdayIndex;
  weekdayLabel: string;
  hourLabel: string;
  value: number;
  playsUnique: number;
  viewsUnique: number;
  conversions?: number;
  revenue?: number;
  deviceDominant: string;
  devicePercentage: number;
}

export interface TopWindow {
  weekdayLabel: string;
  hourLabel: string;
  value: number;
}

export interface DeviceBestWindows {
  desktop: string;
  mobile: string;
  tablet: string;
}

export interface AutoInsight {
  text: string;
}

// Calcular horário campeão (melhor combinação de volume + consistência)
export function calculateChampion(data: PrimeTimesMatrix, metric: PrimeTimeMetric): ChampionWindow {
  const sortedCells = [...data.matrix].sort((a, b) => {
    const aValue = metric === 'plays' ? a.playsUnique : a.viewsUnique;
    const bValue = metric === 'plays' ? b.playsUnique : b.viewsUnique;
    return bValue - aValue;
  });

  const champion = sortedCells[0];
  const value = metric === 'plays' ? champion.playsUnique : champion.viewsUnique;
  const weekday = WEEKDAYS[champion.weekday];
  const bucket = HOUR_BUCKETS[champion.hourBucket];

  // Usar dados reais de dispositivos, conversões e receita
  const deviceDominant = champion.deviceDominant || 'Desktop';
  const devicePercentage = champion.devicePercentage || 0;
  const conversions = champion.conversions || 0;
  const revenue = champion.revenue || 0;

  return {
    weekday: champion.weekday,
    weekdayLabel: weekday.label,
    hourLabel: bucket.label,
    value,
    playsUnique: champion.playsUnique || 0,
    viewsUnique: champion.viewsUnique || 0,
    conversions,
    revenue,
    deviceDominant,
    devicePercentage
  };
}

// Calcular top 3 janelas (incluindo o campeão - do MAIOR para o MENOR)
export function calculateTop4Windows(data: PrimeTimesMatrix, metric: PrimeTimeMetric): TopWindow[] {
  // Ordenar do MAIOR para o MENOR valor
  const sortedCells = [...data.matrix].sort((a, b) => {
    const aValue = metric === 'plays' ? a.playsUnique : a.viewsUnique;
    const bValue = metric === 'plays' ? b.playsUnique : b.viewsUnique;
    return bValue - aValue; // Ordem DECRESCENTE (maior primeiro)
  });

  // Pegar 1º, 2º e 3º lugares (incluindo o campeão)
  const topWindows = sortedCells.slice(0, 3).map(cell => {
    const weekday = WEEKDAYS[cell.weekday];
    const bucket = HOUR_BUCKETS[cell.hourBucket];
    const value = metric === 'plays' ? cell.playsUnique : cell.viewsUnique;

    return {
      weekdayLabel: weekday.label,
      hourLabel: bucket.label,
      value
    };
  });

  // Garantir que está ordenado do maior para o menor
  return topWindows.sort((a, b) => b.value - a.value);
}

// Calcular melhor janela por dispositivo
export function calculateDeviceBestWindows(data: PrimeTimesMatrix, metric: PrimeTimeMetric): DeviceBestWindows {
  // Agrupar células por dispositivo dominante e encontrar pico de cada
  const devicePeaks: Record<string, { value: number; label: string }> = {};

  data.matrix.forEach(cell => {
    const value = metric === 'plays' ? cell.playsUnique : cell.viewsUnique;
    const device = cell.deviceDominant || 'Desktop';
    const bucket = HOUR_BUCKETS[cell.hourBucket];

    if (!devicePeaks[device] || value > devicePeaks[device].value) {
      devicePeaks[device] = {
        value,
        label: bucket.label
      };
    }
  });

  return {
    desktop: devicePeaks['Desktop']?.label || '18h-20h',
    mobile: devicePeaks['Mobile']?.label || '12h-14h',
    tablet: devicePeaks['Tablet']?.label || '16h-18h'
  };
}

// Gerar insight automático baseado nos dados
export function generateAutoInsight(data: PrimeTimesMatrix, metric: PrimeTimeMetric): AutoInsight {
  const sortedCells = [...data.matrix].sort((a, b) => {
    const aValue = metric === 'plays' ? a.playsUnique : a.viewsUnique;
    const bValue = metric === 'plays' ? b.playsUnique : b.viewsUnique;
    return bValue - aValue;
  });

  // Analisar padrões
  const topWindows = sortedCells.slice(0, 10);
  const eveningWindows = topWindows.filter(w => w.hourBucket >= 9); // após 18h
  const weekdays = topWindows.map(w => w.weekday);
  const mondayCount = weekdays.filter(w => w === 0).length;
  const wednesdayCount = weekdays.filter(w => w === 3).length;

  // Texto fixo conforme PRD
  const insight = 'Seu público é mais ativo à noite, especialmente segunda. O tráfego mobile representa 65% das visualizações.';
  
  return { text: insight };
}

// ===== Funções para dados de conversão (Resultado) =====
// Espelham as funções gerais acima, mas operam sobre as métricas de conversão
// (conversions / conversionRate / revenue) já presentes em cada célula da matriz real.

// Pseudo-plays para regularizar (Laplace) a taxa de conversão de células com baixa amostra,
// evitando que um bucket com 1 play / 1 conversão (100%) seja eleito "campeão".
const RATE_SMOOTHING_PRIOR = 20;

// Valor da célula para a métrica de conversão selecionada (usado na EXIBIÇÃO)
export function conversionMetricValue(cell: PrimeTimeData, metric: ConversionMetric): number {
  if (metric === 'conversions') return cell.conversions || 0;
  if (metric === 'revenue') return cell.revenue || 0;
  return cell.conversionRate || 0; // conversionRate
}

// Chave de ORDENAÇÃO por métrica. Para conversionRate usa taxa suavizada (não premia amostra mínima);
// para conversions/revenue é o próprio valor.
function conversionSortKey(cell: PrimeTimeData, metric: ConversionMetric): number {
  if (metric === 'conversions') return cell.conversions || 0;
  if (metric === 'revenue') return cell.revenue || 0;
  return (cell.conversions || 0) / ((cell.playsUnique || 0) + RATE_SMOOTHING_PRIOR);
}

// Horário campeão de conversão: célula com maior valor da métrica selecionada,
// puxando conversões/receita/dispositivo reais daquela célula
export function calculateChampionConversion(data: PrimeTimesMatrix, metric: ConversionMetric): ChampionWindow {
  const sortedCells = [...data.matrix].sort(
    (a, b) => conversionSortKey(b, metric) - conversionSortKey(a, metric)
  );

  const champion = sortedCells[0];
  const weekday = WEEKDAYS[champion.weekday];
  const bucket = HOUR_BUCKETS[champion.hourBucket];

  return {
    weekday: champion.weekday,
    weekdayLabel: weekday.label,
    hourLabel: bucket.label,
    value: conversionMetricValue(champion, metric),
    playsUnique: champion.playsUnique || 0,
    viewsUnique: champion.viewsUnique || 0,
    conversions: champion.conversions || 0,
    revenue: champion.revenue || 0,
    deviceDominant: champion.deviceDominant || 'Desktop',
    devicePercentage: champion.devicePercentage || 0
  };
}

// Top 3 janelas (incluindo o campeão), ordenadas pela chave de ranking; exibe o valor real
export function calculateTop4WindowsConversion(data: PrimeTimesMatrix, metric: ConversionMetric): TopWindow[] {
  const sortedCells = [...data.matrix].sort(
    (a, b) => conversionSortKey(b, metric) - conversionSortKey(a, metric)
  );

  return sortedCells.slice(0, 3).map(cell => ({
    weekdayLabel: WEEKDAYS[cell.weekday].label,
    hourLabel: HOUR_BUCKETS[cell.hourBucket].label,
    value: conversionMetricValue(cell, metric)
  }));
}

// Normaliza o valor de plataforma do backend para as 3 categorias dos cards.
// O player grava apenas "Desktop" e "Celular" (mobile); tablet hoje não é populado.
function normalizeDevice(raw?: string): 'desktop' | 'mobile' | 'tablet' | null {
  const d = (raw || '').toLowerCase();
  if (d.includes('desk') || d.includes('computador')) return 'desktop';
  if (d.includes('celular') || d.includes('mobile') || d.includes('phone') || d.includes('android') || d.includes('ios') || d.includes('smart')) return 'mobile';
  if (d.includes('tab') || d.includes('ipad')) return 'tablet';
  return null;
}

// Melhor janela de conversão por dispositivo dominante (só considera células com valor > 0)
export function calculateDeviceBestWindowsConversion(data: PrimeTimesMatrix, metric: ConversionMetric): DeviceBestWindows {
  const devicePeaks: Record<string, { value: number; label: string }> = {};

  data.matrix.forEach(cell => {
    if (conversionMetricValue(cell, metric) <= 0) return;
    const device = normalizeDevice(cell.deviceDominant);
    if (!device) return;
    const bucket = HOUR_BUCKETS[cell.hourBucket];
    const rank = conversionSortKey(cell, metric);

    if (!devicePeaks[device] || rank > devicePeaks[device].value) {
      devicePeaks[device] = { value: rank, label: bucket.label };
    }
  });

  return {
    desktop: devicePeaks['desktop']?.label || '—',
    mobile: devicePeaks['mobile']?.label || '—',
    tablet: devicePeaks['tablet']?.label || '—'
  };
}

// Insight automático calculado a partir dos dados reais de conversão
export function generateAutoInsightConversion(data: PrimeTimesMatrix, metric: ConversionMetric): AutoInsight {
  const totalConversions = data.matrix.reduce((sum, cell) => sum + (cell.conversions || 0), 0);

  if (totalConversions === 0) {
    return { text: translateInterfaceText("Ainda não há conversões registradas neste período para gerar um insight.") };
  }

  const champion = calculateChampionConversion(data, metric);
  const championFullLabel = WEEKDAYS[champion.weekday].fullLabel.toLowerCase();

  // Dispositivo predominante (por PLAYS) nas janelas que registraram conversões.
  // Atenção: o backend não segmenta conversões por dispositivo, então só afirmamos a
  // predominância de tráfego — não atribuímos uma % de conversões a um device.
  const deviceTraffic: Record<string, number> = {};
  data.matrix.forEach(cell => {
    if ((cell.conversions || 0) <= 0) return;
    const device = cell.deviceDominant || 'Desktop';
    deviceTraffic[device] = (deviceTraffic[device] || 0) + (cell.playsUnique || 0);
  });

  const topDevice = Object.entries(deviceTraffic)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || champion.deviceDominant;

  const metricPhrase = metric === 'revenue'
    ? translateInterfaceText("A maior receita acontece")
    : metric === 'conversionRate'
      ? translateInterfaceText("A melhor taxa de conversão acontece")
      : translateInterfaceText("As conversões se concentram");

  const text = translateInterfaceText("{metric} em {day} no horário {time}. ", { metric: metricPhrase, day: championFullLabel, time: champion.hourLabel })
    + translateInterfaceText("O tráfego nesses períodos é predominantemente {device}.", { device: topDevice });

  return { text };
}
